import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface PrivacyContextType {
  isPrivacyMode: boolean;
  togglePrivacyMode: () => void;
  maskValue: (value: string | number, prefix?: string) => string;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

export const usePrivacy = () => {
  const context = useContext(PrivacyContext);
  if (context === undefined) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
};

interface PrivacyProviderProps {
  children: ReactNode;
}

export const PrivacyProvider: React.FC<PrivacyProviderProps> = ({ children }) => {
  // Load privacy mode state from localStorage
  const [isPrivacyMode, setIsPrivacyMode] = useState(() => {
    const saved = localStorage.getItem('privacy-mode');
    return saved === 'true';
  });

  // Save privacy mode state to localStorage
  useEffect(() => {
    localStorage.setItem('privacy-mode', String(isPrivacyMode));
  }, [isPrivacyMode]);

  const togglePrivacyMode = () => {
    setIsPrivacyMode((prev) => !prev);
  };

  // Helper function to mask values
  const maskValue = (value: string | number, prefix: string = '$'): string => {
    if (!isPrivacyMode) {
      return typeof value === 'string' ? value : String(value);
    }
    // Return masked value with dots
    return `${prefix}••••`;
  };

  const contextValue: PrivacyContextType = {
    isPrivacyMode,
    togglePrivacyMode,
    maskValue,
  };

  return (
    <PrivacyContext.Provider value={contextValue}>
      {children}
    </PrivacyContext.Provider>
  );
};















